import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShieldAlert, 
  CheckCircle2, 
  FileText, 
  Lock, 
  Zap, 
  AlertTriangle, 
  Target, 
  FileCheck,
  ArrowRight,
  HelpCircle,
  Clock,
  Terminal,
  Activity,
  Network
} from 'lucide-react';

const PentestGlobalAudit = () => {
  const navigate = useNavigate();
  
  const springTransition = {
    type: "spring",
    stiffness: 100,
    damping: 20,
    mass: 1
  };

  // 2. Actions concrètes (Vocabulaire technique enrichi)
  const concretely = [
    { title: "Analyse des applications web & failles applicatives", icon: <Zap size={18} /> },
    { title: "Test des systèmes d’authentification & escalade de privilèges", icon: <Lock size={18} /> },
    { title: "Analyse de l’infrastructure et des services exposés", icon: <Network size={18} /> },
    { title: "Cartographie et identification des points d’entrée", icon: <Target size={18} /> }
  ];

  // 8. Types de tests réalisés
  const testTypes = [
    "Pentest applicatif web",
    "Analyse d'exposition de la surface d'attaque extérieure",
    "Audit renforcé des mécanismes d'authentification",
    "Scan de vulnérabilités critiques sur services exposés",
    "Vérification des configurations de sécurité et protocoles"
  ];

  // 3. Risques identifiés
  const risks = [
    "Accès non autorisé aux environnements de production",
    "Fuite de données sensibles et propriété intellectuelle",
    "Compromission de comptes à privilèges élevés",
    "Exploitation de services exposés non patchés"
  ];

  // 3. Impacts potentiels
  const impacts = [
    "Vol de données clients (Non-conformité RGPD)",
    "Intrusion et pivotement dans le système interne",
    "Chiffrement par attaques ransomware",
    "Lourde perte financière et préjudice juridique"
  ];

  // 3. Méthodologie pas à pas
  const methodology = [
    { step: "01", title: "Définition du périmètre", desc: "Identification stricte des systèmes, applications et services IP à tester." },
    { step: "02", title: "Analyse & Collecte", desc: "Cartographie complète de votre surface d'attaque et des ports ouverts." },
    { step: "03", title: "Recherche de Failles", desc: "Identification des vulnérabilités critiques et des mauvaises configurations." },
    { step: "04", title: "Évaluation des Risques", desc: "Analyse d'impact réel et contextualisation des chaînes d'exploitation." },
    { step: "05", title: "Rapport Détaillé", desc: "Restitution technique documentée avec preuves de concept à l'appui." },
    { step: "06", title: "Remédiation", desc: "Fourniture de recommandations de sécurité claires pour guider vos correctifs." }
  ];

  // 4. Livrables
  const deliverables = [
    "Liste exhaustive des vulnérabilités triées par sévérité",
    "Calcul du niveau de criticité (Score CVSS)",
    "Preuves de concept (PoC) d'exploitation technique",
    "Plan de remédiation et recommandations techniques prioritaires"
  ];

  return (
    <section className="w-full bg-[#f8fafc] py-20 px-6 flex flex-col items-center font-inter select-none">
      
      {/* HEADER & CADRE LÉGAL */}
      <div className="text-center mb-16 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-6"
        >
          <span className="text-[#1e3a5f] text-[10px] font-black uppercase tracking-widest">
            Expertise Cybersécurité Offensive
          </span>
        </motion.div>

        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl font-black text-[#0f172a] mb-6 tracking-tight leading-tight"
        >
          Analyse Offensive & Test d'Intrusion
        </motion.h2>

        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-xl max-w-2xl mx-auto inline-block text-left shadow-sm"
        >
          <p className="text-amber-900 text-sm font-bold flex items-center gap-2">
            <Lock size={16} className="text-amber-600 flex-shrink-0" /> Cadre Légal : 
            <span className="font-medium text-amber-800">Les tests d'intrusion sont réalisés strictement après signature d'une autorisation écrite de votre part.</span>
          </p>
        </motion.div>
      </div>

      <div className="w-full max-w-[1050px] space-y-12">
        
        {/* OBJECTIF & CONTEXTE */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Objectif */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springTransition}
            className="lg:col-span-5 p-8 rounded-2xl bg-white border border-slate-200/60 shadow-sm flex flex-col justify-center"
          >
            <h3 className="text-lg font-black text-[#0f172a] mb-3 flex items-center gap-2.5">
              <Target className="text-blue-600" size={20} /> Objectif du Pentest
            </h3>
            <p className="text-slate-600 text-base leading-relaxed font-medium">
              Simuler le mode opératoire d'une attaque réelle pour identifier précisément vos failles techniques et applicatives avant qu'elles ne soient exploitées de manière malveillante.
            </p>
          </motion.div>

          {/* Contexte */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springTransition, delay: 0.1 }}
            className="lg:col-span-7 p-8 rounded-2xl bg-white border border-slate-200/60 shadow-sm"
          >
            <h3 className="text-lg font-black text-[#0f172a] mb-4 flex items-center gap-2.5">
              <HelpCircle className="text-slate-500" size={20} /> Contexte de menace
            </h3>
            <div className="space-y-3 text-slate-600 text-sm font-medium leading-relaxed">
              <p>Les cyberattaques modernes exploitent en priorité les failles connues, les services exposés obsolètes ou les mauvaises configurations réseau non corrigées.</p>
              <p>Une seule vulnérabilité mineure peut suffire à un attaquant déterminé pour compromettre l'intégralité d'une infrastructure et accéder à vos données sensibles.</p>
              <p className="text-slate-900 font-bold bg-slate-50 p-2.5 rounded-lg border-l-2 border-slate-400">
                 Fait : La majorité des incidents critiques reposent sur des erreurs de configuration déjà documentées mais non détectées en interne.
              </p>
            </div>
          </motion.div>
        </div>

        {/* TYPES DE TESTS & ACTIONS CONCRÈTES */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-4">
          {/* Ce que nous faisons */}
          <div className="lg:col-span-6 space-y-4">
            <h3 className="text-xl font-black text-[#0f172a] mb-2">Ce que nous faisons concrètement</h3>
            <div className="grid grid-cols-1 gap-3">
              {concretely.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200/60 bg-white shadow-sm">
                  <div className="p-2 rounded-lg bg-blue-50 text-blue-600 flex-shrink-0">
                    {item.icon}
                  </div>
                  <span className="font-bold text-[#1e3a5f] text-sm">{item.title}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Périmètre d'application */}
          <div className="lg:col-span-6 space-y-4">
            <h3 className="text-xl font-black text-[#0f172a] mb-2">Périmètre technique ciblé</h3>
            <div className="bg-white rounded-2xl p-6 border border-slate-200/60 shadow-sm space-y-3.5">
              {testTypes.map((test, idx) => (
                <div key={idx} className="flex items-start gap-3 text-slate-600 text-sm font-medium">
                  <CheckCircle2 size={16} className="text-[#1e3a5f] mt-0.5 flex-shrink-0" />
                  <span>{test}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SIMULATION D'ATTAQUE (Chaîne d'exploitation Terminal-Style) */}
        <div className="pt-4">
          <h3 className="text-xl font-black text-[#0f172a] mb-6 flex items-center gap-2">
            <Terminal size={20} className="text-slate-700" /> Progression d'une simulation d'attaque type
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 bg-[#0f172a] p-6 rounded-2xl relative overflow-hidden shadow-xl">
            {[
              { label: "1. Surface exposée", desc: "Analyse externe" },
              { label: "2. Vulnérabilité", desc: "Faille identifiée" },
              { label: "3. Exploitation", desc: "Injection de charge" },
              { label: "4. Accès Système", desc: "Élévation de privilèges" },
              { label: "5. Compromission", desc: "Exfiltration simulée" }
            ].map((step, idx) => (
              <div key={idx} className="relative bg-white/5 border border-white/10 p-4 rounded-xl text-center backdrop-blur-sm">
                <span className="text-[10px] font-black uppercase text-blue-400 block mb-1">{step.label}</span>
                <span className="text-white text-xs font-medium">{step.desc}</span>
                {idx < 4 && (
                  <div className="hidden md:flex absolute top-1/2 -right-2 transform -translate-y-1/2 z-20 items-center justify-center bg-blue-500 rounded-full w-4 h-4 shadow-md text-white font-bold text-[8px]">
                    →
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* RISQUES, IMPACTS & CRITICITÉ (Alerte d'impact critique) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
          {/* Risques */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200/60 shadow-sm">
            <h3 className="text-lg font-black text-[#0f172a] mb-4 flex items-center gap-2">
              <AlertTriangle className="text-orange-500" size={18} /> Risques techniques identifiés
            </h3>
            <div className="space-y-3">
              {risks.map((risk, idx) => (
                <div key={idx} className="flex items-start gap-2.5 text-slate-600 text-sm font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-1.5 flex-shrink-0" />
                  <span>{risk}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Impacts Potentiels */}
          <div className="bg-red-50/40 p-6 rounded-2xl border border-red-100 shadow-sm">
            <h3 className="text-lg font-black text-red-900 mb-4 flex items-center gap-2">
              <ShieldAlert className="text-red-600" size={18} /> Impacts business critiques
            </h3>
            <div className="space-y-3">
              {impacts.map((impact, idx) => (
                <div key={idx} className="flex items-start gap-2.5 text-red-900/80 text-sm font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-600 mt-1.5 flex-shrink-0" />
                  <span>{impact}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* MÉTHODOLOGIE DE MISSION (Timeline fluide) */}
        <div className="pt-4">
          <h3 className="text-xl font-black text-[#0f172a] mb-6 flex items-center gap-2">
            <Activity size={20} className="text-blue-600" /> Comment se déroule la mission ?
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {methodology.map((m, idx) => (
              <div key={idx} className="bg-white p-5 rounded-xl border border-slate-200/60 shadow-sm hover:border-blue-300 transition-all group">
                <span className="text-xs font-black text-blue-500/80 block mb-1 group-hover:text-blue-600 transition-colors">{m.step}</span>
                <h4 className="font-bold text-[#0f172a] text-sm mb-1.5">{m.title}</h4>
                <p className="text-slate-500 text-xs leading-relaxed font-medium">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CE QUE VOUS RECEVEZ (Livrables Audit) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 rounded-3xl bg-[#1e3a5f] text-white relative overflow-hidden shadow-xl"
        >
          <div className="relative z-10">
            <h3 className="text-xl font-black mb-6 flex items-center gap-3">
              <FileCheck size={22} className="text-blue-300" /> Rapports & livrables d'audit remis
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {deliverables.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-white/10 p-4 rounded-xl border border-white/5 backdrop-blur-sm">
                  <FileText size={18} className="text-blue-300 mt-0.5 flex-shrink-0" />
                  <span className="font-bold text-sm leading-snug">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 blur-[100px] rounded-full -mr-20 -mt-20" />
        </motion.div>

        {/* POURQUOI AGIR MAINTENANT & CTA TARIFS */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch pt-4">
          {/* Urgence */}
          <div className="md:col-span-5 bg-white p-6 rounded-3xl border border-slate-200/60 shadow-sm flex flex-col justify-center">
            <h3 className="text-base font-black text-[#0f172a] mb-3 flex items-center gap-2">
              <Clock className="text-orange-500" size={16} /> Pourquoi agir maintenant ?
            </h3>
            <div className="space-y-3 text-xs text-slate-500 font-medium leading-relaxed">
              <p>Les cyberattaques ne ciblent plus uniquement les grandes structures. Les PME et startups forment désormais le gros des cibles car elles sont jugées moins protégées.</p>
              <p className="text-orange-900 font-bold bg-orange-50/60 p-2 rounded-lg border-l-2 border-orange-400">
                Attendre la première compromission ou l'apparition d'un ransomware coûte systématiquement plus cher que de la prévenir en amont.
              </p>
            </div>
          </div>

          {/* Tarification Sur Devis & Prise de contact */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="md:col-span-7 bg-white p-8 rounded-3xl border-2 border-dashed border-slate-300/80 text-center flex flex-col items-center justify-center bg-gradient-to-br from-white to-slate-50"
          >
            <span className="text-slate-400 font-black uppercase tracking-widest text-[10px] mb-2">Modèle de Tarification</span>
            <p className="text-[#0f172a] text-3xl font-black mb-3">Sur devis</p>
            <p className="text-slate-500 text-xs max-w-sm mb-6 font-medium leading-relaxed">
              Chaque périmètre applicatif et réseau étant unique, nos tarifs s'adaptent sur-mesure au volume de cibles IP et à la complexité de vos technos.
            </p>
            <button 
              onClick={() => navigate('/rdv')}
              className="bg-[#1e3a5f] text-white px-8 py-3.5 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-[#0f172a] hover:shadow-xl transition-all duration-300 active:scale-95 group"
            >
              Planifier un test d'intrusion 
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default PentestGlobalAudit;